import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { useSystemSetting } from "../components/shared/useSystemSetting.js";
import {
  getMarketingWorklist,
  listAssessorContacts,
  logAssessorContact,
  subscribeAssessor,
  unsubscribeAssessor,
  listEntityRows,
  listUsers,
  getCurrentUser,
} from "../services/apiClient.js";
import { formatDate } from "../utils/date.js";

// Marketing Phone Calls (legacy MarketingPhCalls.aspx + SetupMarketingPhoneCalls
// merged into one page): the assessors GPS keeps in touch with — mostly the
// HOUSE list (legacy "Resource Department") — with last-contact info, the
// weekly chase (red = not phoned this week, Sunday-start like legacy), inline
// call/email logging (who-called is derived from the signed-in user), history
// per row, and add/remove. Rules: docs/business-rules/workflow.md (Marketing).

function MarketingCallsPage() {
  const [rows, setRows] = useState(null);
  const [pm, setPm] = useState("");
  const [users, setUsers] = useState([]);
  const [assessors, setAssessors] = useState([]);
  const [me, setMe] = useState(null);
  const [openId, setOpenId] = useState(null); // expanded assessor row
  const [history, setHistory] = useState([]);
  const [log, setLog] = useState({ kind: "phone", comments: "", successful: false });
  const [addId, setAddId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // ONE switch, and it is the operating one: `marketing_calls_enabled` is what
  // an Administrator turns off during a busy period. The reports master switch
  // used to gate this page as well and made it read "not built yet" — that pen
  // was removed entirely on 2026-08-07 (Robert).
  const marketingOn = useSystemSetting("marketing_calls_enabled");

  async function load(owner = pm) {
    setError("");
    try {
      setRows(await getMarketingWorklist(owner));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the marketing list.");
    }
  }
  useEffect(() => {
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    listEntityRows("assessors").then(setAssessors).catch(() => {});
    getCurrentUser().then(setMe).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marketing switched off (System Settings): the page still works if someone
  // lands here, but says why the counters and menu entry have gone quiet.
  const marketingBanner =
    marketingOn === false ? (
      <Alert variant="secondary" className="py-2">
        Marketing phone calls are currently switched <strong>off</strong> in System
        Settings — the dashboard counters and menu entry are hidden until an
        Administrator turns them back on. Logging calls here still works.
      </Alert>
    ) : null;

  async function openRow(r) {
    if (openId === r.assessor_id) {
      setOpenId(null);
      return;
    }
    setOpenId(r.assessor_id);
    setLog({ kind: "phone", comments: "", successful: false });
    try {
      setHistory(await listAssessorContacts(r.assessor_id));
    } catch {
      setHistory([]);
    }
  }

  async function saveContact(assessorId) {
    setBusy(true);
    setError("");
    try {
      await logAssessorContact({ assessor_id: assessorId, ...log, comments: log.comments || null });
      setLog({ kind: "phone", comments: "", successful: false });
      setHistory(await listAssessorContacts(assessorId));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to log the contact.");
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    if (!addId) return;
    setBusy(true);
    try {
      // New entries default to the HOUSE list (how 1.0 actually used it).
      await subscribeAssessor({ assessor_id: Number(addId) });
      setAddId("");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to add the assessor.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(r) {
    if (!window.confirm(`Remove ${r.name} from the update list? The contact history stays.`)) return;
    try {
      await unsubscribeAssessor(r.subscription_id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to remove.");
    }
  }

  const subscribedIds = new Set((rows || []).map((r) => r.assessor_id));
  const dueCount = (rows || []).filter((r) => !r.phoned_this_week).length;

  return (
    <section className="page-panel">
      {marketingBanner}
      <div className="d-flex flex-wrap align-items-end column-gap-3 row-gap-2 no-print">
        <div className="page-heading"><h2>Marketing Phone Calls <RulesHelp topic="marketingCalls" /></h2></div>
        <span className="small text-muted pb-2">
          {rows ? `${dueCount} of ${rows.length} still to phone this week (week starts Sunday)` : ""}
        </span>
        <div className="ms-auto d-flex align-items-end column-gap-2">
          <div>
            <Form.Label className="mb-0 small text-muted">List</Form.Label>
            <ThemedSelect size="sm" style={{ width: 180 }} value={pm}
              onChange={(e) => { setPm(e.target.value); load(e.target.value); }}>
              <option value="">All</option>
              <option value="house">House list</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </ThemedSelect>
          </div>
          <div>
            <Form.Label className="mb-0 small text-muted">Add assessor</Form.Label>
            <ThemedSelect size="sm" style={{ width: 220 }} value={addId} onChange={(e) => setAddId(e.target.value)}>
              <option value="">— pick —</option>
              {assessors.filter((a) => !subscribedIds.has(a.id)).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </ThemedSelect>
          </div>
          <Button size="sm" variant="success" onClick={add} disabled={busy || !addId}>+ Add</Button>
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!rows ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <Table striped bordered hover size="sm" className="mb-0 align-middle">
          <thead className="table-surface">
            <tr>
              <th>Assessor</th>
              <th>Company</th>
              <th>Phone</th>
              <th>List</th>
              <th>Last Phoned</th>
              <th>Last Emailed</th>
              <th className="text-center">This Week</th>
              <th className="text-end" style={{ width: 210 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <>
                {/* No row tint — the This Week badge carries the due signal. */}
                <tr key={r.subscription_id}>
                  <td className="fw-semibold">{r.name}</td>
                  <td className="text-truncate" style={{ maxWidth: 160 }} data-hint={r.company_name || undefined}>{r.company_name || "-"}</td>
                  <td className="text-nowrap">{r.phone || "-"}</td>
                  <td className="text-nowrap small">{r.owner}</td>
                  <td className="text-nowrap">{r.last_phone ? formatDate(r.last_phone) : <span className="text-danger">never</span>}</td>
                  <td className="text-nowrap">{r.last_email ? formatDate(r.last_email) : "-"}</td>
                  <td className="text-center">
                    {r.phoned_this_week ? <Badge bg="success">✓</Badge> : <Badge bg="danger">due</Badge>}
                  </td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant={openId === r.assessor_id ? "secondary" : "outline-primary"}
                      className="me-2" onClick={() => openRow(r)}>
                      {openId === r.assessor_id ? "Close" : "Log / History"}
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(r)}>Remove</Button>
                  </td>
                </tr>
                {openId === r.assessor_id ? (
                  <tr key={`x-${r.subscription_id}`}>
                    <td colSpan={8}>
                      <div className="d-flex flex-wrap align-items-end column-gap-2 row-gap-2 mb-2">
                        <ThemedSelect size="sm" style={{ width: 110 }} value={log.kind}
                          onChange={(e) => setLog({ ...log, kind: e.target.value })}>
                          <option value="phone">Phone</option>
                          <option value="email">Email</option>
                        </ThemedSelect>
                        <Form.Control size="sm" style={{ minWidth: 280, flex: 1 }} placeholder="Comments"
                          value={log.comments} onChange={(e) => setLog({ ...log, comments: e.target.value })} />
                        <Form.Check type="checkbox" id={`ok-${r.assessor_id}`} label="Spoke to them"
                          checked={log.successful}
                          onChange={(e) => setLog({ ...log, successful: e.target.checked })} />
                        <Button size="sm" onClick={() => saveContact(r.assessor_id)} disabled={busy}>
                          Log {log.kind === "phone" ? "Call" : "Email"}
                        </Button>
                        {me ? <span className="small text-muted">logged as {me.full_name}</span> : null}
                      </div>
                      {history.length ? (
                        <div className="small">
                          {history.map((h, i) => (
                            <div key={i}>
                              {formatDate(h.contacted_on)} · {h.kind}{h.successful ? " ✓" : ""}
                              {h.logged_by_name ? ` · ${h.logged_by_name}` : ""}
                              {h.comments ? ` — ${h.comments}` : ""}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="small text-muted">No contact history.</span>
                      )}
                    </td>
                  </tr>
                ) : null}
              </>
            ))}
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted">No assessors on this list.</td></tr>
            ) : null}
          </tbody>
        </Table>
      )}
    </section>
  );
}

export default MarketingCallsPage;
